import { Api } from "../../../../Api";
import { DataFrame, Method, Status } from "../../../../DataFrame";
import { DependencyContainer } from "../../../../DependencyContainer";
import { ExalusConnectionService } from "../../../ExalusConnectionService";
import { ResponseResult } from "../../../FieldChangeResult";
import { IExalusConnectionService } from "../../../IExalusConnectionService";
import { ILoggerService } from "../../../Logging/ILoggerService";
import { LoggerService } from "../../../Logging/LoggerService";
import { ChatService, ChatServiceErrorCode } from "../../ChatService";
import { IMessage, MessageHandler } from "../../IChatService";
import { ControllerChatSender, ControllerMessageSource, ControllerMessageType } from "../ControllerChat/IControllerChat";
import { IFamillyChat } from "./IFamillyChat";


export class FamillyChat implements IFamillyChat {

    private _connection: IExalusConnectionService;
    private _chatSubscriptions: MessageHandler<any>[] = [];

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);

        let removeSubCallback = this._connection.SubscribeTo<ControllerChatMessageResponse>("/homemessaging/notify/message/new", (frame) => {
            if (frame.Resource === "/homemessaging/notify/message/new") {
                const data = frame.Data as ControllerChatMessageResponse;

                if (data.RecipientUserGuid == null && data.MessageType == ControllerMessageType.Message) {
                    const res = new FamillyChatMessage();

                    res.DateTime = data.DateTime;
                    res.Guid = data.Guid;
                    res.MessageText = data.MessageText;
                    res.SenderName = data.SenderName ? data.SenderName : "n/a";

                    this._chatSubscriptions.forEach(handler => {
                        handler(res);
                    })
                }
            }
        });
    }

    async GetCachedMessagesAsync(): Promise<ResponseResult<ChatServiceErrorCode> | IMessage[]> {
        try {
            const resp = await this._connection.SendAndWaitForResponseAsync<ControllerChatMessageResponse[]>(new GetMessagesRequest(), 12000, false);

            if (resp == null)
                return new ResponseResult(ChatServiceErrorCode.OtherError, `Cannot get messages - response is null.`);

            if (resp.Status != Status.OK)
                return new ResponseResult(ChatServiceErrorCode.OtherError, `Cannot get messages - controller responded with response code ${resp.Status}.`);

            if (resp.Data == null)
                return new ResponseResult(ChatServiceErrorCode.NoData, `Cannot get messages - controller responded with status OK, but response does not contain data!`);


            const result: FamillyChatMessage[] = [];

            resp.Data.forEach(d => {
                if (d.RecipientUserGuid == null && d.MessageType == ControllerMessageType.Message) {
                    const res = new FamillyChatMessage();

                    res.DateTime = d.DateTime;
                    res.Guid = d.Guid;
                    res.MessageText = d.MessageText;
                    res.SenderName = d.SenderName ? d.SenderName : "n/a";

                    result.push(res);
                }
            });

            return result;
        } catch (error) {
            return new ResponseResult(ChatServiceErrorCode.FatalError, `Cannot get messages - exception occurred ${error}`);
        }
    }

    async SendMessageAsync(message: string): Promise<Status> {
        try {
            const result = await this._connection.SendAndWaitForResponseAsync<ControllerChatMessageResponse[]>(new SendMessagesRequest(message), 8000, false);

            if (result == null || result.Status == null)
                return Status.FatalError;

            return result.Status;

        } catch (error) {
            DependencyContainer.Log?.Error(ChatService.ServiceName, `Cannot send message! ${error}`);
            return Status.FatalError;
        }
    }

    SubscribeToMessages<T extends IMessage>(messageHandler: MessageHandler<T>): void {
        this._chatSubscriptions.push(messageHandler);
    }

    UnsubscribeFromMessages<T extends IMessage>(messageHandler: MessageHandler<T>): void {
        if (this._chatSubscriptions)
            this._chatSubscriptions = this._chatSubscriptions.filter(h => h !== messageHandler);
    }

}

class ControllerChatMessageResponse {
    Guid: string = "";
    Sender: ControllerChatSender = ControllerChatSender.Controller;
    MessageType: ControllerMessageType = ControllerMessageType.Info;
    MessageSource: ControllerMessageSource = ControllerMessageSource.Other;
    SenderUserGuid: string = "";
    RecipientUserGuid: string = "";
    MessageText: string = "";
    TextToSpeechMessage: string = "";
    SenderName: string = "";
    DateTime: string = new Date().toDateString();
    MessageActions: any;
    MessageActionsData: any;
}

class FamillyChatMessage implements IMessage {
    private _guid = "";
    private _senderName = "";
    private _messageText = "";
    private _dateTime = "";

    get Guid(): string {
        return this._guid;
    }
    get SenderName(): string {
        return this._senderName;
    }
    get MessageText(): string {
        return this._messageText;
    }
    get DateTime(): string {
        return this._dateTime;
    }

    set Guid(value: string) {
        this._guid = value;
    }
    set SenderName(value: string) {
        this._senderName = value;
    }
    set MessageText(value: string) {
        this._messageText = value;
    }
    set DateTime(value: string) {
        this._dateTime = value;
    }
}

class SendMessagesRequest extends DataFrame<string> {
    constructor(message: string) {
        super();
        this.Resource = "/homemessaging/message";
        this.Method = Method.Put;
        this.Data = message;
    }
}

class GetMessagesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/homemessaging/messages";
        this.Method = Method.Get;
    }
}